import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, Button, Select, Table, StatusBadge, Modal, Textarea } from '@/components/ui';
import {
  MessageSquare,
  PartyPopper,
  Building2,
  Mail,
  Phone,
  Calendar,
  Users,
  Eye,
} from 'lucide-react';
import { format } from 'date-fns';

type InquiryType = 'contact' | 'party' | 'rental';

interface BaseInquiry {
  id: string;
  name: string;
  email: string;
  phone?: string;
  status: string;
  notes?: string;
  createdAt: string;
}

interface ContactInquiry extends BaseInquiry {
  type: 'contact';
  subject: string;
  message: string;
}

interface PartyInquiry extends BaseInquiry {
  type: 'party';
  preferredDate: string;
  partyType: string;
  guestCount: number;
  partyNotes?: string;
}

interface RentalInquiry extends BaseInquiry {
  type: 'rental';
  organizationName: string;
  eventType: string;
  preferredDate: string;
  attendees: number;
  avNeeds?: string;
  rentalNotes?: string;
}

type Inquiry = ContactInquiry | PartyInquiry | RentalInquiry;

const statusOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'new', label: 'New' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'spam', label: 'Spam' },
];

const tabs: { id: InquiryType; label: string; icon: React.ReactNode }[] = [
  { id: 'contact', label: 'Contact', icon: <MessageSquare className="h-4 w-4" /> },
  { id: 'party', label: 'Party Zone', icon: <PartyPopper className="h-4 w-4" /> },
  { id: 'rental', label: 'Rentals', icon: <Building2 className="h-4 w-4" /> },
];

export function InquiriesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const activeTab = (searchParams.get('type') as InquiryType) || 'contact';
  const statusFilter = searchParams.get('status') || '';

  const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null);

  const { data: inquiries, isLoading } = useQuery<Inquiry[]>({
    queryKey: ['inquiries', activeTab, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      const response = await api.get(`/admin/inquiries/${activeTab}?${params}`);
      const items = response.data?.data?.items || response.data?.data || [];
      return items.map((item: unknown) => ({ ...item as object, type: activeTab }));
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status, notes }: { id: string; status: string; notes?: string }) => {
      await api.put(`/admin/inquiries/${activeTab}/${id}/status`, { status, notes });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
  });

  function setTab(type: InquiryType) {
    setSearchParams({ type, ...(statusFilter && { status: statusFilter }) });
  }

  function setStatus(status: string) {
    setSearchParams({ type: activeTab, ...(status && { status }) });
  }

  const baseColumns = [
    {
      key: 'name',
      header: 'Contact',
      render: (inquiry: Inquiry) => (
        <div>
          <p className="font-medium text-white">{inquiry.name}</p>
          <p className="text-sm text-gray-500">{inquiry.email}</p>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (inquiry: Inquiry) => <StatusBadge status={inquiry.status} />,
    },
    {
      key: 'date',
      header: 'Received',
      render: (inquiry: Inquiry) => (
        <span className="text-gray-400">
          {format(new Date(inquiry.createdAt), 'MMM d, yyyy')}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-20',
      render: (inquiry: Inquiry) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedInquiry(inquiry);
          }}
        >
          <Eye className="h-4 w-4" />
        </Button>
      ),
    },
  ];

  const contactColumns = [
    ...baseColumns.slice(0, 1),
    {
      key: 'subject',
      header: 'Subject',
      render: (inquiry: Inquiry) => (
        <span className="text-gray-300 truncate max-w-xs block">{(inquiry as ContactInquiry).subject}</span>
      ),
    },
    ...baseColumns.slice(1),
  ];

  const partyColumns = [
    ...baseColumns.slice(0, 1),
    {
      key: 'details',
      header: 'Details',
      render: (inquiry: Inquiry) => (
        <div>
          <p className="text-gray-300">{(inquiry as PartyInquiry).partyType}</p>
          <p className="text-sm text-gray-500">{(inquiry as PartyInquiry).guestCount} guests</p>
        </div>
      ),
    },
    {
      key: 'preferredDate',
      header: 'Preferred Date',
      render: (inquiry: Inquiry) => (
        <span className="text-gray-400">
          {format(new Date((inquiry as PartyInquiry).preferredDate), 'MMM d, yyyy')}
        </span>
      ),
    },
    ...baseColumns.slice(1),
  ];

  const rentalColumns = [
    ...baseColumns.slice(0, 1),
    {
      key: 'organization',
      header: 'Organization',
      render: (inquiry: Inquiry) => (
        <div>
          <p className="text-gray-300">{(inquiry as RentalInquiry).organizationName}</p>
          <p className="text-sm text-gray-500">{(inquiry as RentalInquiry).eventType}</p>
        </div>
      ),
    },
    {
      key: 'attendees',
      header: 'Attendees',
      render: (inquiry: Inquiry) => (
        <span className="text-gray-300">{(inquiry as RentalInquiry).attendees}</span>
      ),
    },
    ...baseColumns.slice(1),
  ];

  const columns =
    activeTab === 'contact'
      ? contactColumns
      : activeTab === 'party'
      ? partyColumns
      : rentalColumns;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Inquiries</h1>
        <p className="text-gray-400 mt-1">Manage customer inquiries and requests</p>
      </div>

      {/* Tabs & Filters */}
      <Card padding="md">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <div className="flex gap-2">
            {tabs.map((tab) => (
              <Button
                key={tab.id}
                variant={activeTab === tab.id ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => setTab(tab.id)}
              >
                {tab.icon}
                <span className="ml-2">{tab.label}</span>
              </Button>
            ))}
          </div>
          <div className="w-full md:w-48">
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={(e) => setStatus(e.target.value)}
            />
          </div>
        </div>
      </Card>

      {/* Inquiries Table */}
      <Card padding="none">
        <Table
          columns={columns}
          data={inquiries ?? []}
          keyField="id"
          loading={isLoading}
          onRowClick={setSelectedInquiry}
          emptyMessage="No inquiries found"
        />
      </Card>

      {/* Detail Modal */}
      <InquiryDetailModal
        inquiry={selectedInquiry}
        onClose={() => setSelectedInquiry(null)}
        onUpdateStatus={(status, notes) => {
          if (selectedInquiry) {
            updateStatusMutation.mutate(
              { id: selectedInquiry.id, status, notes },
              { onSuccess: () => setSelectedInquiry(null) }
            );
          }
        }}
        isUpdating={updateStatusMutation.isPending}
      />
    </div>
  );
}

// Detail Modal Component
interface InquiryDetailModalProps {
  inquiry: Inquiry | null;
  onClose: () => void;
  onUpdateStatus: (status: string, notes?: string) => void;
  isUpdating: boolean;
}

function InquiryDetailModal({
  inquiry,
  onClose,
  onUpdateStatus,
  isUpdating,
}: InquiryDetailModalProps) {
  const [notes, setNotes] = useState('');
  const [newStatus, setNewStatus] = useState('');

  // Reset when inquiry changes
  useEffect(() => {
    if (inquiry) {
      setNotes(inquiry.notes || '');
      setNewStatus(inquiry.status);
    }
  }, [inquiry]);

  if (!inquiry) return null;

  return (
    <Modal isOpen={!!inquiry} onClose={onClose} title="Inquiry Details" size="lg">
      <div className="space-y-6">
        {/* Contact Info */}
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-midnight-100">
              <Mail className="h-4 w-4 text-gray-400" />
            </div>
            <div>
              <p className="text-sm text-gray-400">Email</p>
              <a href={`mailto:${inquiry.email}`} className="text-amber-400 hover:underline">
                {inquiry.email}
              </a>
            </div>
          </div>
          {inquiry.phone && (
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-midnight-100">
                <Phone className="h-4 w-4 text-gray-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Phone</p>
                <a href={`tel:${inquiry.phone}`} className="text-amber-400 hover:underline">
                  {inquiry.phone}
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Type-specific details */}
        {inquiry.type === 'contact' && (
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-400 mb-1">Subject</p>
              <p className="text-white">{inquiry.subject}</p>
            </div>
            <div>
              <p className="text-sm text-gray-400 mb-1">Message</p>
              <p className="text-gray-300 whitespace-pre-wrap">{inquiry.message}</p>
            </div>
          </div>
        )}

        {inquiry.type === 'party' && (
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-midnight-100">
                <Calendar className="h-4 w-4 text-gray-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Preferred Date</p>
                <p className="text-white">
                  {format(new Date(inquiry.preferredDate), 'MMMM d, yyyy')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-midnight-100">
                <Users className="h-4 w-4 text-gray-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Guests</p>
                <p className="text-white">{inquiry.guestCount}</p>
              </div>
            </div>
            <div className="col-span-2">
              <p className="text-sm text-gray-400 mb-1">Party Type</p>
              <p className="text-white">{inquiry.partyType}</p>
            </div>
            {inquiry.partyNotes && (
              <div className="col-span-2">
                <p className="text-sm text-gray-400 mb-1">Additional Notes</p>
                <p className="text-gray-300">{inquiry.partyNotes}</p>
              </div>
            )}
          </div>
        )}

        {inquiry.type === 'rental' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-400 mb-1">Organization</p>
                <p className="text-white">{inquiry.organizationName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400 mb-1">Event Type</p>
                <p className="text-white">{inquiry.eventType}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400 mb-1">Preferred Date</p>
                <p className="text-white">
                  {format(new Date(inquiry.preferredDate), 'MMMM d, yyyy')}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-400 mb-1">Attendees</p>
                <p className="text-white">{inquiry.attendees}</p>
              </div>
            </div>
            {inquiry.avNeeds && (
              <div>
                <p className="text-sm text-gray-400 mb-1">A/V Needs</p>
                <p className="text-gray-300">{inquiry.avNeeds}</p>
              </div>
            )}
            {inquiry.rentalNotes && (
              <div>
                <p className="text-sm text-gray-400 mb-1">Additional Notes</p>
                <p className="text-gray-300">{inquiry.rentalNotes}</p>
              </div>
            )}
          </div>
        )}

        {/* Status & Notes */}
        <div className="pt-4 border-t border-midnight-100 space-y-4">
          <Select
            label="Status"
            options={statusOptions.slice(1)}
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value)}
          />
          <Textarea
            label="Internal Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Add notes about this inquiry..."
          />
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-midnight-100">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => onUpdateStatus(newStatus, notes)}
            loading={isUpdating}
            disabled={newStatus === inquiry.status && notes === (inquiry.notes || '')}
          >
            Update
          </Button>
        </div>
      </div>
    </Modal>
  );
}
